import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Bell, LogOut, ChevronDown, AlertTriangle, WifiOff } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import archonWS from "../../lib/websocket";
import SyncStatus from "../SyncStatus";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [systemAlerts, setSystemAlerts] = useState([]);

  const fetchCounts = useCallback(async () => {
    try {
      const [notifRes, statusRes] = await Promise.all([
        api.get("/notifications/unread-count"),
        api.get("/notifications/system-status").catch(() => ({ data: { alerts: [] } })),
      ]);
      setUnreadCount(notifRes.data.count);
      setSystemAlerts(statusRes.data.alerts || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);

    // Connect WebSocket
    const token = localStorage.getItem("archon_token");
    if (token) {
      archonWS.connect(token);
      const unsub = archonWS.on("message", (msg) => {
        if (msg.event === "notification") {
          setUnreadCount((c) => c + 1);
        }
      });
      return () => { clearInterval(interval); unsub(); };
    }
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const handleLogout = async () => {
    archonWS.disconnect();
    await logout();
    navigate("/login");
  };

  return (
    <header
      data-testid="header"
      className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-20"
    >
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-medium text-gray-800" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
          Sales Operating System
        </h2>
        {systemAlerts.length > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold">
            <AlertTriangle size={10} /> {systemAlerts.length} alert{systemAlerts.length > 1 ? "s" : ""}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <SyncStatus />

        <button
          data-testid="notification-bell"
          onClick={() => navigate("/notifications")}
          className="relative p-2 rounded hover:bg-gray-100 transition-colors"
        >
          <Bell size={18} strokeWidth={1.5} className="text-gray-600" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 text-[10px] flex items-center justify-center"
            >
              {unreadCount}
            </Badge>
          )}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
            >
              <div className="w-7 h-7 rounded-sm bg-[#0052CC] text-white text-xs font-bold flex items-center justify-center">
                {user?.name?.charAt(0) || "U"}
              </div>
              <span className="text-sm text-gray-700 hidden sm:block">{user?.name}</span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5 text-xs text-gray-500">
              {user?.email}
            </div>
            <DropdownMenuItem
              data-testid="logout-button"
              onClick={handleLogout}
              className="text-red-600 cursor-pointer"
            >
              <LogOut size={14} className="mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
